// build-panel-view.ts — the in-game CAROUSEL BUILD panel DOM overlay (HTML/DOM UI shell
// plan, Phase 5 — build/order/recruit STEP 3 + 4). ONE view, three `variant`s:
//   - 'unit' (FORM D, MobileUnitBuildForm game/Builder.cpp:488-914): the VTS (kidfBuildVehicle
//     1015 "ORDER VEHICLE") and the HRC (kidfBuildInfantry 1011 "RECRUIT PERSONNEL") — the
//     SAME class on the SAME 160×143 layout, cyItem=17, SPEED/RANGE stat rows, LIVE per-item
//     queue-count + yellow build-progress overlays, Order / Cancel Order / Close buttons.
//   - 'structure' (FORM C, StructureBuildForm game/Headquarters.cpp:315-532 / buildui.ts:986):
//     kidfBuildStructure (1017 "ORDER BUILDING"), cyItem=18 (NOT 17 — C-R5), P DEMAND/P SUPPLY
//     stat rows (NOT speed/range — C-R6), NO per-item queue/progress overlays (the canvas
//     SetQueueInfo(null,null) — C-R5), a single "Build" button + "Close" (NO Order/Cancel —
//     C-R10). "Build" RETURNS A GOB TYPE (EndForm(gt)) and ENQUEUES NOTHING; the HQ
//     continuation hands gt to gpfrmPlace (the CANVAS placement overlay, FORM F not ported).
//   - 'research' (FORM E, UpgradeForm game/Research.cpp:433-590 / buildui.ts:877): kidfUpgrade
//     (1045 "R&D TECHNOLOGIES"), cyItem=18, the SIMPLEST carousel — ONLY a COST meter row (NO
//     armor/damage/speed/range/power — E-R3) + a PREREQUISITES label + multiline value + the
//     DESCRIPTION; 2 items (Advanced HRC / Advanced VTS, icons hrc1/vts1, already-owned
//     omitted); an empty state ("NO UPGRADES AVAILABLE" → Research hidden — E-R3). A single
//     "Research" button + "Close" (NO Order/Cancel). "Research" RETURNS THE upgm (EndForm(upgm))
//     and ENQUEUES NOTHING; the gob-menu Research continuation enqueues kmidUpgradeCommand
//     (E-R5) in buildui.ts (U1).
//
// The canvas form is a PLAIN Form (NOT a ShellForm, NOT a DialogForm) in BOTH variants
// (taxonomy T1/T2):
//   - it does NOT zip (only ShellForm zips — T1 / C-R1 / D-R1);
//   - it is SCREEN-CENTRED at the TOP (rc.left = (dibW - formW)/2 & ~1; rc.top = 0 — C-R2 /
//     buildui.ts:1008-1011,1942-1948 SetOwner) — NOT over the gob, NOT centred vertically;
//   - its body is the OPAQUE buildformbkgd.png backdrop (OnPaintBackground), NO black
//     DrawBorder, NO side-colour title bar (C-R3 / D-R1);
//   - kfFrmAutoTakedown (C-R4): a tap on the backdrop dismisses it to kidcCancel.
//
// COMMAND-ADJACENT, DISPLAY-ONLY: this view emits NO command and constructs NO Message (U1).
// It re-polls a pure read-model (the form's BuildModel via getModel) on the canvas DefUpdate
// cadence so the (unit) queue counts + progress bar + limit label track the sim (D-R5); the
// choices report through onSelect / onOrder / onCancelOrder (unit) / onBuild (structure),
// which the host routes to the SAME OnControlSelected the canvas continuation uses (the EXACT
// kmidBuildOther/kmidAbortBuildOther enqueue — unit — stays in buildui.ts; the structure
// "Build" enqueues NOTHING, it returns a gt).
//
// DESIGN-PREF SPLIT (T3): web font for the TEXT labels/heading/stat-rows; REAL BITMAPS for
// the meter GLYPHS — the unit ICONS (the 'icon' sprite frame, side-recoloured), the PIP meter
// (PipMeterControl.s_ptbmPip = pip.png, the small cyan circle, tiled contiguously) and the
// DAMAGE row (DamageMeterControl's per-target-class glyphs — star / vehicle / structure),
// ALL sized at game px × the chrome magnify (main.ts kUiMagnify ≈ the design scale s / 2, so
// the bitmaps keep their proportion to the form) — NOT the design scale s directly. The pip/damage COUNTS still come from the
// canvas GetPipCount / DamagePipCount (D-R3); only the RENDERING is the real bitmap. A CSS
// fallback (cyan circles) covers a pack with no pip/damage art.

import { type ButtonArt, composeButtonDataUrl } from '../title-art.ts';
import { ksfxGuiButtonTap } from '../../ui/uikit.ts';
import {
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  FONT_TITLE,
  FONT_BUTTON,
  FONT_BODY,
  buttonWidthU,
  formScale,
  rootBox,
} from '../chrome.ts';
import type { BuildPanelModel, BuildPanelItem } from '../../ui/buildui.ts';

// forms.ini.pp kidfBuildVehicle/kidfBuildInfantry/kidfBuildStructure (FORM 0 0 160 143).
const FORM = { w: 160, h: 143 };
// the ANIMLIST kidcList (-1 17 28 121) — cyItem=17 (unit) / 18 (structure — C-R5).
const LIST = { x: -1, y: 17, w: 28, h: 121 };
const CYITEM = { unit: 17, structure: 18, research: 18 };
// the close buttons (top-right): Help (135 3 10 12) / Cancel (147 3 10 12).
const HELP = { x: 135, y: 3, w: 10, h: 12 };
const CANCEL = { x: 147, y: 3, w: 10, h: 12 };
// FIX E: the forms.ini LABEL is (2 5 82 0); widen the box toward the ?/X buttons (they start
// at x=135) so "ORDER BUILDING" at the larger 7.5*s title size renders full (not condensed by
// fitToWidth). x=2 → ~125 design units of clear space before the header buttons.
const HEADING = { x: 2, y: 5, w: 125 };
const NAME = { x: 30, y: 19, w: 75 };
// the stat ROWS: a label on the left (x=30), a value on the right (x=136, right-aligned),
// a meter in the middle (x=64). y per row; the DAMAGE row is a damage meter (no value lbl).
const ROW = { labelX: 30, labelW: 22, valueX: 136, valueW: 22, meterX: 64, meterW: 71, meterH: 9 };
// the per-row y (shared design ys: cost 31, armor 40, damage 49, then two more at 58/67).
const ROWS = { cost: 31, armor: 40, damage: 49, r4: 58, r5: 67 };
const DAMAGE_METER = { x: 64, y: 49, w: 94, h: 9 };
const DESC = { x: 30, y: 79, w: 127, h: 8 };
// the bottom button row — UNIT variant (kidfBuildVehicle/Infantry):
const LIMIT_UNIT = { x: 33, y: 123, w: 32, h: 12 };
const ORDER = { x: 30, y: 124, w: 32, h: 12 };
const CANCEL_ORDER = { x: 65, y: 124, w: 59, h: 12 };
const CLOSE_UNIT = { x: 127, y: 124, w: 31, h: 12 };
// the bottom button row — STRUCTURE variant (kidfBuildStructure): Build (86,124,34,12),
// Close (124,124,34,12), limit (30,127,93,12) "Building Limit Reached" (C-R8 geometry).
const LIMIT_STRUCT = { x: 30, y: 127, w: 93, h: 12 };
const BUILD = { x: 86, y: 124, w: 34, h: 12 };
const CLOSE_STRUCT = { x: 124, y: 124, w: 34, h: 12 };
// the RESEARCH variant (kidfUpgrade "R&D TECHNOLOGIES", FORM E). NO armor/damage/speed/range/
// power rows — ONLY a COST meter (label 30,31 / value 136,31 / meter 64,31,71,9 — shared ROW/
// ROWS.cost), then DESCRIPTION (30,49,127) + a PREREQUISITES label (30,97) + multiline value
// (30,106,127). The buttons are "Research" (78,124,42,12) + "Close" (124,124,34,12).
const RESEARCH_DESC = { x: 30, y: 49, w: 127, h: 8 };
const PREREQ_LABEL = { x: 30, y: 97, w: 22 };
const PREREQ_VALUE = { x: 30, y: 106, w: 127, h: 8 };
const RESEARCH = { x: 78, y: 124, w: 42, h: 12 };
const CLOSE_RESEARCH = { x: 124, y: 124, w: 34, h: 12 };

// FIX B: the stat/prereq labels are now baked WHITE (kifntShadow), same as the values —
// the old muted COL_LABEL gray is gone.
// the WI source faces (out/fontid): FONT_BODY = Eurostile (kifntStandard — labels,
// values, the NAME, descriptions); FONT_BUTTON = Eurostile (kifntButton — buttons);
// FONT_TITLE = Quadtrangle (kifntTitle — the form HEADING). FONT stays the body face
// so the many label()/rowLabel() call-sites read in the standard face by default.
const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;
// the CSS pip-meter FALLBACK colour, used only when the real pip.png bitmap is absent: the
// canvas pip.png is a small CYAN circle (border-radius:50%), NOT a green square. The damage
// fallback glyphs are per-class cyan-ish glyphs (the real bitmaps are preferred).
const COL_PIP = '#8fd0e0';
const COL_DMG = { infantry: '#bfe8f0', vehicle: '#8fd0e0', structure: '#6fb0c8' };
// the gap (game px) the canvas DamageMeterControl leaves between glyphs (DrawPips:
// px.v += sizPip.cx + 1 — misccontrols.ts:1969). The pip meter blits contiguously (no gap).
const kDamageGap = 1;

// the canvas re-runs DefUpdate on the sim cadence; the unit-menu uses 25ms. Match that so
// the queue counts + progress bar + limit label refresh live (D-R5).
const kRefreshMs = 25;

/** a game-px bitmap (the CPU present path's RGBA buffer) — what the canvas blits. */
export type Bitmap = { cx: number; cy: number; rgba: Uint8Array };

export interface BuildPanelArt {
  /** the opaque buildformbkgd.png backdrop (the form body) — RGBA. null ⇒ a CSS panel. */
  backdrop: Bitmap | null;
  /** the REAL meter glyph bitmaps the canvas blits (PipMeterControl.s_ptbmPip = pip.png, the
   *  small cyan circle; DamageMeterControl's per-target-class glyphs — damage_infantry.png
   *  "star", damage_vehicle.png, damage_structure.png). The view tiles `count` of each (the
   *  count comes from GetPipCount / DamagePipCount), sized native-px so they read EXACTLY like
   *  the canvas (cyan spaced circles / class glyphs), instead of CSS squares. null ⇒ the CSS
   *  fallback (a cyan circle for pips, a per-class coloured glyph for damage). */
  pip?: Bitmap | null;
  damageInfantry?: Bitmap | null;
  damageVehicle?: Bitmap | null;
  damageStructure?: Bitmap | null;
}

export interface BuildPanelViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the CURRENT read-model, or null when the panel should close (builder gone). The view
   *  polls this on the refresh cadence — the carousel + meters + buttons track the sim. */
  getModel: () => BuildPanelModel | null;
  /** a row was tapped → select it. unit: the host routes to the list Select →
   *  OnControlNotify → UpdateUnitInfo; a 2nd tap on the selected row orders (D-R6).
   *  structure: just selects + UpdateStructureInfo (no tap-to-order — Build is a button). */
  onSelect: (ut: number) => void;
  /** UNIT variant: the Order button → onOrder (OnControlSelected(kidcOrder) — the EXACT
   *  kmidBuildOtherCommand enqueue stays in buildui.ts). Absent for the structure variant. */
  onOrder?: () => void;
  /** UNIT variant: the Cancel Order button → onCancelOrder (OnControlSelected(kidcCancelOrder)
   *  — the kmidAbortBuildOtherCommand enqueue). Absent for the structure variant. */
  onCancelOrder?: () => void;
  /** STRUCTURE variant: the "Build" button → onBuild. This does NOT enqueue (C-R10/C-R11):
   *  the host ends the form with the selected structure's gt and hands it to gpfrmPlace
   *  (the CANVAS placement overlay). Absent for the unit variant. */
  onBuild?: () => void;
  /** RESEARCH variant: the "Research" button → onResearch. This does NOT enqueue (E-R5/U1):
   *  the host ends the form with the selected upgrade's upgm and the gob-menu Research
   *  continuation enqueues kmidUpgradeCommand in buildui.ts. Absent for unit/structure. */
  onResearch?: () => void;
  /** Close / backdrop tap / Esc → the host ends the form (GetLastSelection persistence). */
  onClose: () => void;
  /** the header "?" Help button → open the SECTION-ONLY DOM help panel (the host's domHelp).
   *  The help OVERLAYS this build panel (it stays open underneath); closing help returns
   *  here. Absent ⇒ the "?" stays inert (the canvas-help-not-ported fallback). */
  onHelp?: () => void;
  /** the chrome magnification (main.ts kUiMagnify ≈ the design scale s / 2): game px → CSS px
   *  for the unit ICON bitmaps + the list border/progress bars, so they keep their proportion
   *  to the formScale-sized panel and grow with it on phones (T3). */
  magnify: number;
  /** the form backdrop + button art (faithful chrome; absent ⇒ CSS fallbacks). */
  art?: BuildPanelArt;
  /** the header ?/X button bitmaps (forms.ini kidcHelp helpup/helpdown.png + kidcCancel
   *  cancelup/canceldown.png — the SAME cyan-framed rounded button, ?/X glyph baked in;
   *  data-URLs). The view blits these as bitmap buttons (FIX A) instead of flat CSS boxes;
   *  absent ⇒ the flat-box fallback. */
  headerArt?: { helpUp: string; helpDown: string; cancelUp: string; cancelDown: string };
  /** the 9-slice button pieces for the text buttons (Order/Cancel Order/Close). */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

export class BuildPanelView {
  private readonly d: BuildPanelViewDeps;
  private timer: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  /** the design-unit scale (the SAME fixed family the other in-game DOM forms use). */
  private s = 1;
  /** the stage (the 160×143 panel) — rebuilt on first paint + when the item SET changes
   *  (a new buildable appears / the order/limit/cancel buttons toggle); repositioned every
   *  poll is unnecessary (it is screen-centred at top). The per-item LIVE overlays (queue
   *  count, progress bar) + the stat block + the limit/order visibility update in place. */
  private stage: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  /** the icon data-URL cache (keyed by ut) — icons are stable per unit type. */
  private readonly iconCache = new Map<number, string>();
  private readonly btnCache = new Map<string, string>();
  /** the meter-glyph data-URL cache (keyed by the bitmap identity — the pip + 3 damage
   *  glyphs are stable for the life of the view). */
  private readonly glyphCache = new Map<Bitmap, string>();
  /** a fingerprint of the last-built item SET (ut+disabled list) so we only rebuild rows
   *  when the buildable set changes, not every 25ms tick. */
  private lastItemsKey = '';
  /** the model variant ('unit' FORM D / 'structure' FORM C) — selects cyItem, the stat
   *  rows, the per-item overlays, and the bottom button row. Read off the model each paint
   *  (the host only ever feeds one variant per mounted view, but this stays robust). */
  private variant: 'unit' | 'structure' | 'research' = 'unit';

  private cyItem(): number {
    return CYITEM[this.variant];
  }

  constructor(d: BuildPanelViewDeps) {
    this.d = d;
    const st = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20). The canvas
    // MobileUnitBuildForm is kfFrmAutoTakedown, so a tap OUTSIDE the panel dismisses it
    // (= kidcCancel). pointer-events:all so the click reaches us (not the canvas).
    st.position = 'fixed';
    st.inset = '0';
    st.zIndex = '20';
    st.color = COL_FG;
    st.fontFamily = FONT;
    st.userSelect = 'none';
    st.pointerEvents = 'all';
    st.background = 'transparent';
    st.overflow = 'hidden';
    d.root.addEventListener('pointerdown', this.onBackdrop);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render(); // first paint
    this.timer = setInterval(this.tick, kRefreshMs);
  }

  // a tap NOT on the stage → close (AutoTakedown → kidcCancel).
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-buildpanel-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.close();
  };

  // Esc → close (the FORM default kidcCancel).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  };

  private readonly onResize = (): void => {
    this.lastItemsKey = '';
    this.render();
  };

  destroy(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private close(): void {
    if (this.closed) return;
    this.closed = true;
    this.d.onClose();
  }

  private readonly tick = (): void => {
    if (this.closed) return;
    const model = this.d.getModel();
    if (model === null) {
      this.close();
      return;
    }
    this.render(model);
  };

  // the SAME fixed magnification family as the other in-game DOM forms (chrome.ts
  // formScale, at the 160×143 form size). (Distinct from `magnify` = kUiMagnify ≈ s/2, which
  // sizes the game-px ICON bitmaps — T3.) NOTE: this view's `at` reads this.s (not an
  // `s` param) and its textBtn stop-propagates + uses 0.04em tracking inside the
  // AutoTakedown backdrop, so those stay LOCAL (sharing would change call sites/output).
  private scale(): number {
    return formScale();
  }

  private render(model?: BuildPanelModel | null): void {
    const m = model === undefined ? this.d.getModel() : model;
    if (m === null) {
      this.close();
      return;
    }

    this.variant = m.variant;
    this.s = this.scale();
    // rebuild the stage when the item SET / button visibility changes; else update live.
    const key =
      m.items.map((it) => `${it.ut}:${it.disabled ? 1 : 0}`).join(',') +
      `|${m.variant}|${m.heading}|${m.orderShown ? 1 : 0}|${m.cancelOrderShown ? 1 : 0}|` +
      `${m.limitReached ? 1 : 0}|${m.emptyState ? 1 : 0}|${this.s}`;
    if (this.stage === null || key !== this.lastItemsKey) {
      this.buildStage(m);
      this.lastItemsKey = key;
      this.d.root.replaceChildren(this.stage!);
      // the HEADING ("ORDER VEHICLE" / "R&D TECHNOLOGIES", Quadtrangle): the canvas title
      // font is compact and fills the 82-unit box; the wider Quadtrangle web face overflows,
      // so scaleX it down to fit (anchored left) now the stage is in the DOM — never cut.
      const h = this.stage!.querySelector('[data-buildpanel-heading]') as HTMLElement | null;
      if (h !== null) fitToWidth(h);
    }
    // LIVE updates that happen every poll (no rebuild): the icon list overlays
    // (queue count + progress bar + selection border) + the stat block.
    this.updateList(m);
    this.updateInfo(m);
  }

  // position a child at forms.ini design coords (x, y[, w, h]) within the stage.
  private at(child: HTMLElement, x: number, y: number, w?: number, h?: number): HTMLElement {
    const s = this.s;
    Object.assign(child.style, { position: 'absolute', left: `${x * s}px`, top: `${y * s}px` });
    if (w !== undefined) child.style.width = `${w * s}px`;
    if (h !== undefined) child.style.height = `${h * s}px`;
    return child;
  }

  private buildStage(m: BuildPanelModel): void {
    const s = this.s;
    const stageW = FORM.w * s,
      stageH = FORM.h * s;

    // the stage = the form body: the OPAQUE buildformbkgd.png backdrop (NO border, NO
    // title bar — FORM D / C-R3). The CSS fallback is an opaque dark panel.
    const stage = el('div', {
      position: 'absolute',
      width: `${stageW}px`,
      height: `${stageH}px`,
      boxSizing: 'border-box',
      overflow: 'hidden',
      background: this.d.art?.backdrop ? '#000' : 'rgba(16,22,26,0.97)',
      imageRendering: 'pixelated',
    });
    stage.dataset.buildpanelStage = '1';
    if (this.d.art?.backdrop) {
      const bg = el('img', {
        position: 'absolute',
        left: '0',
        top: '0',
        width: `${stageW}px`,
        height: `${stageH}px`,
        imageRendering: 'pixelated',
        pointerEvents: 'none',
      }) as HTMLImageElement;
      bg.src = rgbaToDataUrl(this.d.art.backdrop);
      bg.draggable = false;
      stage.append(bg);
    }

    // the HEADING ("ORDER VEHICLE" / "RECRUIT PERSONNEL", kifntTitle) — Quadtrangle. LEFT-
    // aligned (the forms.ini LABEL is left-anchored at x=2 — do NOT centre, so don't use the
    // shared chrome.formTitle which centres). FIX E: match the other DOM titles' size/tracking
    // (chrome.formTitle uses 7.5*s + 0.05em letterSpacing + -0.32em wordSpacing) so common
    // headings render full-size like the canvas. overflow:visible + fitToWidth still guards a
    // heading wider than its (now widened) box.
    const heading = el('div', {
      fontFamily: FONT_TITLE,
      fontSize: `${Math.round(7.5 * s)}px`,
      fontWeight: '400',
      letterSpacing: '0.05em',
      wordSpacing: '-0.32em',
      color: '#fff',
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      transformOrigin: 'left center',
    });
    heading.textContent = m.heading;
    heading.dataset.buildpanelHeading = '1';
    this.at(heading, HEADING.x, HEADING.y, HEADING.w);
    // nudge the rendered heading +1px right / -2px up (literal CSS px) to register on the
    // canvas backdrop's baked title position. Adjust left/top (NOT transform) so it composes
    // cleanly with fitToWidth's scaleX(...) condense (set later in render()).
    heading.style.left = `${HEADING.x * s + 1}px`;
    heading.style.top = `${HEADING.y * s - 3}px`;
    stage.append(heading);

    // the header buttons (Help + Cancel) top-right (forms.ini kidcHelp helpup/helpdown.png +
    // kidcCancel cancelup/canceldown.png — the SAME cyan-framed rounded button with the ?/X
    // glyph). FIX A: render the REAL bitmaps (ButtonControl blits helpup/cancelup, swaps to
    // *down on press) when headerArt is present, else the flat-box CSS fallback. The Cancel
    // (X) dismisses to kidcCancel; the Help (?) opens the SECTION-ONLY DOM help panel (onHelp
    // → the host's domHelp, mirroring the canvas Help(anchor) — Builder.cpp:703) OVER this
    // build panel, which stays open underneath. (When onHelp is absent — e.g. an isolated
    // preview — the "?" stays inert but still looks ENABLED: its bitmap IS the enabled frame.)
    // the rendered ?/X buttons are 2 CSS px too tall vs the canvas — trim the height by 2px,
    // keeping the top edge anchored (the canvas ButtonControl blits helpup/cancelup at the
    // rect top, misccontrols.cpp:160-165, so trimming from the bottom matches it).
    const help = this.headerBtn(
      '?',
      this.d.headerArt?.helpUp,
      this.d.headerArt?.helpDown,
      true,
      () => {
        this.d.onHelp?.();
      },
    ); // headerBtn's own click plays ksfxGuiButtonTap — don't double it
    help.dataset.buildpanelHelp = '1';
    this.at(help, HELP.x, HELP.y, HELP.w, HELP.h);
    help.style.height = `${HELP.h * s - 2}px`;
    stage.append(help);
    const cancel = this.headerBtn(
      'X',
      this.d.headerArt?.cancelUp,
      this.d.headerArt?.cancelDown,
      true,
      () => this.close(),
    );
    cancel.dataset.buildpanelCancel = '1';
    this.at(cancel, CANCEL.x, CANCEL.y, CANCEL.w, CANCEL.h);
    cancel.style.height = `${CANCEL.h * s - 2}px`;
    stage.append(cancel);

    // the ANIMLIST icon column — a vertically-scrolling list of unit-type icon rows.
    // FIX D: BuildListControl::OnPaint draws a 1-DIB-px kiclrListBorder cyan border around
    // the whole list + fills the interior with kiclrListBackground rgb(16,40,43) (Builder.cpp
    // :1004-1005). The DIB-px border is × magnify (NOT × s). The canvas TOP-ALIGNS the items
    // (ListControl::OnPaint, misccontrols.cpp:1018-1033 walks from y = rc.top, NO centering;
    // the list rect [kidfBuildStructure] kidcList is (-1 17 28 121), no large top inset) — so
    // always anchor to the top (flex-start), the small top margin coming only from the list
    // position + the 1px border.
    const mag = this.d.magnify;
    const list = el('div', {
      position: 'absolute',
      overflowY: 'auto',
      overflowX: 'hidden',
      left: `${(LIST.x + 1) * s}px`,
      top: `${LIST.y * s}px`,
      width: `${LIST.w * s}px`,
      height: `${LIST.h * s}px`,
      boxSizing: 'border-box',
      border: `${Math.max(1, Math.round(mag))}px solid rgb(84,160,172)`,
      background: 'rgb(16,40,43)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
    });
    list.dataset.buildpanelList = '1';
    for (const it of m.items) list.append(this.itemRow(it));
    stage.append(list);
    this.listEl = list;

    // the NAME (labels web font; meters real-bitmap pips). The canvas LabelControl draws the
    // FULL name even when it overruns its nominal forms.ini width (the long R&D name runs
    // nearly the whole panel) — so span from NAME.x to the right margin, NOT the 75-unit
    // nominal, and never clip (overflow visible). NOTE: for the research variant the NAME
    // doubles as the "NO UPGRADES AVAILABLE" empty-state text (E-R3).
    const nameW = FORM.w - NAME.x - 2; // x=30 → ~2px right margin: the full available width
    const name = this.label(NAME.x, NAME.y, nameW, '700', '#fff');
    name.style.overflow = 'visible';
    name.style.transformOrigin = 'left center'; // shrink-to-fit anchors at the left edge
    name.style.top = `${NAME.y * this.s - 2}px`; // nudge up 2px to match the canvas baseline
    name.dataset.buildpanelName = '1';
    stage.append(name);

    if (this.variant === 'research') {
      // FORM E (R&D TECHNOLOGIES): ONLY a COST meter row (NO armor/damage/speed/range/power —
      // E-R3), the DESCRIPTION, and a PREREQUISITES label + multiline value.
      this.statRow(stage, 'COST', ROWS.cost, 'cost', 'pip');

      // the DESCRIPTION at the research y (30,49) — multiline web font.
      const rdesc = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        lineHeight: '1.25',
        overflow: 'hidden',
      });
      rdesc.dataset.buildpanelDescription = '1';
      stage.append(
        this.at(rdesc, RESEARCH_DESC.x, RESEARCH_DESC.y, RESEARCH_DESC.w, RESEARCH_DESC.h * 4),
      );

      // the PREREQUISITES label (30,97) + multiline value (30,106,127) — hidden in the empty
      // state (UpdateUpgradeInfo/SetOwner Show — E-R3).
      // FIX B: the PREREQUISITES label is kifntShadow white too (consistent with the stat labels).
      const preLbl = this.label(PREREQ_LABEL.x, PREREQ_LABEL.y, ROW.meterW, '600', '#fff');
      preLbl.textContent = 'PREREQUISITES';
      preLbl.dataset.buildpanelPrereqLabel = '1';
      stage.append(preLbl);
      const preVal = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        lineHeight: '1.25',
        overflow: 'hidden',
      });
      preVal.dataset.buildpanelPrerequisites = '1';
      stage.append(
        this.at(preVal, PREREQ_VALUE.x, PREREQ_VALUE.y, PREREQ_VALUE.w, PREREQ_VALUE.h * 4),
      );

      this.buildResearchButtons(stage, m);
    } else {
      // the stat rows. SHARED: COST / ARMOR / DAMAGE. Then the variant-specific pair:
      //   unit → SPEED (58) / RANGE (67)  ·  structure → P DEMAND (58) / P SUPPLY (67) (C-R6).
      this.statRow(stage, 'COST', ROWS.cost, 'cost', 'pip');
      this.statRow(stage, 'ARMOR', ROWS.armor, 'armor', 'pip');
      this.statRow(stage, 'DAMAGE', ROWS.damage, 'damage', 'damage');
      if (this.variant === 'structure') {
        this.statRow(stage, 'P DEMAND', ROWS.r4, 'powerDemand', 'pip');
        this.statRow(stage, 'P SUPPLY', ROWS.r5, 'powerSupply', 'pip');
      } else {
        this.statRow(stage, 'SPEED', ROWS.r4, 'speed', 'pip');
        this.statRow(stage, 'RANGE', ROWS.r5, 'range', 'pip');
      }

      // the DESCRIPTION ("This unit/building requires: …" when disabled) — multiline web font.
      // Give it ALL the height between the stat block (DESC.y) and the bottom button row
      // (y=124, ORDER/BUILD): the wider WI body face wrapped the longer structure blurbs
      // (e.g. the Galaxite Processor) past the old fixed 4-line box, clipping them. The
      // modal/form is unchanged — only the description's own box grows to fill the gap.
      // (The unit variant shares this geometry — same button-row y — so it doesn't regress.)
      const descH = ORDER.y - DESC.y - 2; // 124 - 79 - 2 = 43 design units
      const desc = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        lineHeight: '1.25',
        overflow: 'hidden',
      });
      desc.dataset.buildpanelDescription = '1';
      stage.append(this.at(desc, DESC.x, DESC.y, DESC.w, descH));

      if (this.variant === 'structure') this.buildStructButtons(stage, m);
      else this.buildUnitButtons(stage, m);
    }

    // POSITION: horizontally centred; vertically placed like the E-Com overlay — its CENTRE at
    // viewport/3 (floored at 8px) — instead of flush to the TOP (the canvas anchor, D-R1 /
    // buildui.ts:1942-1948). The top-flush anchor slips under the iPhone top safe area (notch /
    // Dynamic Island); the vh/3 placement clears it without needing safe-area insets (matches
    // ecom-view.render()). Centred within the PLAYFIELD box — the root's box (the window in
    // the shipped client; the play pane in the mission editor — chrome.rootBox).
    const vp = rootBox(this.d.root);
    stage.style.left = `${Math.round(Math.max(0, (vp.width - stageW) / 2))}px`;
    stage.style.top = `${Math.round(Math.max(8, vp.height / 3 - stageH / 2))}px`;

    this.stage = stage;
  }

  // UNIT variant bottom row (FORM D): the limit label + Order / Cancel Order / Close.
  private buildUnitButtons(stage: HTMLElement, m: BuildPanelModel): void {
    const limit = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5 * this.s)}px`,
      fontWeight: '700',
      color: '#f0c050',
      textAlign: 'center',
      textShadow: shadow,
      lineHeight: '1.05',
      display: m.limitReached ? 'block' : 'none',
    });
    limit.textContent = 'Limit Reached';
    limit.dataset.buildpanelLimit = '1';
    stage.append(this.at(limit, LIMIT_UNIT.x, LIMIT_UNIT.y, LIMIT_UNIT.w, LIMIT_UNIT.h));

    // Order / Cancel Order / Close — each SIZED to its label via the shared buttonWidthU (the
    // wide WI button face overran the fixed forms.ini widths, e.g. "Cancel Order" touching the
    // 9-slice frame). minW=24 suits the small build-panel buttons (vs the shell default 36).
    // Close stays RIGHT-anchored at the canvas right edge (CLOSE_UNIT right = 127+31 = 158);
    // Order + Cancel Order are mutually exclusive (one shown at a time), anchored at the left
    // slot (ORDER.x) and clamped so the long "Cancel Order" can't overlap Close.
    const wClose = buttonWidthU('Close', CLOSE_UNIT.h, this.s, 24);
    const closeLeft = CLOSE_UNIT.x + CLOSE_UNIT.w - wClose;
    const close = this.textBtn('Close', wClose, CLOSE_UNIT.h, () => this.close());
    close.dataset.buildpanelClose = '1';
    stage.append(this.at(close, closeLeft, CLOSE_UNIT.y));

    const wOrder = Math.min(closeLeft - 3 - ORDER.x, buttonWidthU('Order', ORDER.h, this.s, 24));
    const order = this.textBtn('Order', wOrder, ORDER.h, () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      this.d.onOrder?.();
    });
    order.dataset.buildpanelOrder = '1';
    order.style.display = m.orderShown ? '' : 'none';
    stage.append(this.at(order, ORDER.x, ORDER.y));

    // the cancel-order button: LABEL "Cancel" (was "Cancel Order", which overran into Close);
    // its idc/handler stay the cancel-order action (onCancelOrder → kmidAbortBuildOther). Sized
    // via buttonWidthU off the shorter label, then CENTERED in the gap between Order's right edge
    // (its left slot when Order is hidden) and Close's left edge — so it no longer overlaps Order.
    const wCancel = buttonWidthU('Cancel', CANCEL_ORDER.h, this.s, 24);
    // ALWAYS center against Order's right edge (even when Order is hidden — i.e. ordering maxed
    // out), so Cancel keeps the SAME position whether or not Order is shown (no shift on max-out).
    const orderRight = ORDER.x + wOrder;
    const cancelX = orderRight + (closeLeft - orderRight - wCancel) / 2;
    const cancelOrder = this.textBtn('Cancel', wCancel, CANCEL_ORDER.h, () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      this.d.onCancelOrder?.();
    });
    cancelOrder.dataset.buildpanelCancelOrder = '1';
    cancelOrder.style.display = m.cancelOrderShown ? '' : 'none';
    stage.append(this.at(cancelOrder, cancelX, CANCEL_ORDER.y));
  }

  // STRUCTURE variant bottom row (FORM C): the "Building Limit Reached" label + Build /
  // Close. NO Order / Cancel Order (C-R10). "Build" returns a gt — the host does the
  // gpfrmPlace placement handoff; this view enqueues NOTHING (C-R11/U1).
  private buildStructButtons(stage: HTMLElement, m: BuildPanelModel): void {
    const limit = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5 * this.s)}px`,
      fontWeight: '700',
      color: '#f0c050',
      textAlign: 'center',
      textShadow: shadow,
      lineHeight: '1.05',
      display: m.limitReached ? 'block' : 'none',
    });
    limit.textContent = 'Building Limit Reached';
    limit.dataset.buildpanelLimit = '1';
    stage.append(this.at(limit, LIMIT_STRUCT.x, LIMIT_STRUCT.y, LIMIT_STRUCT.w, LIMIT_STRUCT.h));

    // FIX F: the C++ Build + Close are EQUAL slots — make them a UNIFORM-width pair (no
    // clamp/squish of Build below Close). The pair width fits the wider of the two labels;
    // Close stays right-anchored at the canvas right edge (CLOSE_STRUCT right = 124+34 = 158),
    // Build sits one gap (≈4u) to its left, both at the pair width.
    // the "Build" button (kidcOk "Build" per forms.ini). orderShown = kidcOk visible (a
    // non-disabled item selected && !limit — C-R8). The tap runs onBuild → EndForm(gt) →
    // the CANVAS placement overlay (FORM F); NO command from here.
    const gap = 4;
    const wPair = Math.max(
      buttonWidthU('Build', BUILD.h, this.s, 24),
      buttonWidthU('Close', CLOSE_STRUCT.h, this.s, 24),
    );
    const closeLeft = CLOSE_STRUCT.x + CLOSE_STRUCT.w - wPair;
    const buildLeft = closeLeft - gap - wPair;
    const build = this.textBtn('Build', wPair, BUILD.h, () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      this.d.onBuild?.();
    });
    build.dataset.buildpanelBuild = '1';
    build.dataset.buildpanelOrder = '1'; // shared probe handle (wiUi buildPanelOrderShown)
    build.style.display = m.orderShown ? '' : 'none';
    stage.append(this.at(build, buildLeft, BUILD.y));

    const close = this.textBtn('Close', wPair, CLOSE_STRUCT.h, () => this.close());
    close.dataset.buildpanelClose = '1';
    stage.append(this.at(close, closeLeft, CLOSE_STRUCT.y));
  }

  // RESEARCH variant bottom row (FORM E): the "Research" + "Close" buttons. NO Order / Cancel
  // Order, NO limit label (E-R3). "Research" returns the upgm — the host's gob-menu Research
  // continuation enqueues kmidUpgradeCommand (E-R5); this view enqueues NOTHING (U1). The
  // Research button is hidden in the empty state / for a disabled selection (orderShown).
  private buildResearchButtons(stage: HTMLElement, m: BuildPanelModel): void {
    // FIX F (research variant): Research + Close are a UNIFORM-width pair (the EQUAL-slot C++
    // layout — no clamp/squish). The pair width fits the wider label ("Research"); Close stays
    // right-anchored at the canvas right edge (CLOSE_RESEARCH right = 124+34 = 158), Research
    // sits one gap (≈4u) to its left, both at the pair width.
    // the "Research" button (kidcOk "Research" per forms.ini). orderShown = kidcOk visible
    // (a non-disabled upgrade selected && upgrades available — E-R3/E-R4).
    const gap = 4;
    const wPair = Math.max(
      buttonWidthU('Research', RESEARCH.h, this.s, 24),
      buttonWidthU('Close', CLOSE_RESEARCH.h, this.s, 24),
    );
    const closeLeft = CLOSE_RESEARCH.x + CLOSE_RESEARCH.w - wPair;
    const researchLeft = closeLeft - gap - wPair;
    const research = this.textBtn('Research', wPair, RESEARCH.h, () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      this.d.onResearch?.();
    });
    research.dataset.buildpanelResearch = '1';
    research.dataset.buildpanelOrder = '1'; // shared probe handle (wiUi *PanelOrderShown)
    research.style.display = m.orderShown ? '' : 'none';
    stage.append(this.at(research, researchLeft, RESEARCH.y));

    const close = this.textBtn('Close', wPair, CLOSE_RESEARCH.h, () => this.close());
    close.dataset.buildpanelClose = '1';
    stage.append(this.at(close, closeLeft, CLOSE_RESEARCH.y));
  }

  // a single icon-list row: the side-recoloured unit ICON sized by kGameScale (T3), with
  // the selection border (2px white selected / 1px else — DrawItem :1842-1851), the
  // disabled shadow (:1864 — fDisabled → Shadow), and slots for the LIVE overlays (the
  // queue-count + the yellow build-progress bar) updated by updateList().
  private itemRow(it: BuildPanelItem): HTMLElement {
    const s = this.s;
    const rowH = this.cyItem() * s;
    const row = el('button', {
      position: 'relative',
      display: 'block',
      width: '100%',
      height: `${rowH}px`,
      padding: '0',
      margin: '0',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      boxSizing: 'border-box',
      // FIX G: keep the fixed row height under the list's flex column (don't shrink to fit).
      flexShrink: '0',
    }) as HTMLButtonElement;
    row.dataset.buildpanelItem = String(it.ut);
    row.dataset.buildpanelDisabled = it.disabled ? '1' : '0';

    // the selection / list border frame (filled in updateList).
    const frame = el('div', {
      position: 'absolute',
      inset: '0',
      boxSizing: 'border-box',
      pointerEvents: 'none',
    });
    frame.dataset.buildpanelFrame = '1';
    row.append(frame);

    // the ICON — game-px bitmap × magnify (kGameScale), centred in the row (the canvas
    // centres the frame in the item area — DrawItem :1862-1863).
    if (it.icon !== null) {
      const mag = this.d.magnify;
      const iw = Math.round(it.icon.cx * mag),
        ih = Math.round(it.icon.cy * mag);
      const img = el('img', {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: `${iw}px`,
        height: `${ih}px`,
        transform: 'translate(-50%, -50%)',
        imageRendering: 'pixelated',
        pointerEvents: 'none',
      }) as HTMLImageElement;
      img.src = this.iconUrl(it.ut, it.icon);
      img.draggable = false;
      img.dataset.buildpanelIcon = '1';
      row.append(img);
    }

    // the disabled SHADOW (a translucent black overlay over the whole item).
    const shade = el('div', {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      background: 'rgba(0,0,0,0.5)',
      display: it.disabled ? 'block' : 'none',
    });
    shade.dataset.buildpanelShade = '1';
    row.append(shade);

    // the LIVE per-item overlays — UNIT variant ONLY. The structure carousel sets
    // SetQueueInfo(null,null) (C-R5), so it has NO queue-count + NO build-progress bar:
    // don't even create them for the structure variant (so nothing can leak in).
    if (this.variant === 'unit') {
      // the yellow BUILD-PROGRESS bar. Builder.cpp:1049-1053 insets it by 2 DIB px on every
      // side (rc.left=x+2, rc.bottom=y+cy-2, rc.right=x+cx-2) at height kcyBuildProgress (=3,
      // Builder.cpp:5). Those are DIB px → × magnify (NOT × s, which = 2 × magnify — the bar
      // was ~2× too big). width set live in updateList (= inner width − 2*(2*mag)).
      const mag = this.d.magnify;
      const inset = Math.round(2 * mag);
      const prog = el('div', {
        position: 'absolute',
        left: `${inset}px`,
        bottom: `${inset}px`,
        height: `${Math.max(1, Math.round(3 * mag))}px`,
        background: '#f0e000',
        pointerEvents: 'none',
        display: 'none',
      });
      prog.dataset.buildpanelProgress = '1';
      row.append(prog);

      // the per-item QUEUE-COUNT overlay (Builder.cpp:1056-1059: kifntButton, drawn at
      // x+cx-2-cxQ, y+cy-2-fontHeight — 2 DIB-px insets, game-px font). DIB px → × magnify.
      const cnt = el('div', {
        position: 'absolute',
        right: `${Math.round(3 * mag)}px`,
        bottom: `${Math.round(2 * mag)}px`,
        fontFamily: FONT,
        fontSize: `${Math.max(8, Math.round(8 * mag))}px`,
        fontWeight: '700',
        color: '#fff',
        textShadow: shadow,
        pointerEvents: 'none',
        display: 'none',
      });
      cnt.dataset.buildpanelCount = '1';
      row.append(cnt);
    }

    // select-on-tap → the host routes to the form's list Select (or order on a 2nd tap).
    row.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });
    row.addEventListener('click', (e) => {
      e.stopPropagation();
      this.d.playSfx?.(ksfxGuiButtonTap);
      this.d.onSelect(it.ut);
    });
    // DOUBLE-click a STRUCTURE/research item → select it AND Build/Research (the click-then-Build
    // shortcut). NOT for the UNIT variant: there a 2nd tap of an already-selected unit ALREADY
    // orders (DomSelect → knNotifySelectionTap "tap-on-selected Order" D-R6, buildui.ts:2873), so a
    // dblclick that also calls onSelect+onOrder would enqueue the order two or three times (the
    // "jumps by more than one" double-count). Gated on !disabled so a disabled item can't be built
    // via dblclick (the canvas hides Build for disabled; DomBuild doesn't re-check). onSelect →
    // DomSelect is synchronous, so the following onBuild acts on THIS item.
    if (this.variant !== 'unit') {
      row.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        if (it.disabled) return;
        this.d.onSelect(it.ut);
        this.d.onBuild?.();
      });
    }
    return row;
  }

  // LIVE per-poll list refresh: the selection border, the queue count, the progress bar.
  private updateList(m: BuildPanelModel): void {
    const list = this.listEl;
    if (list === null) return;
    // FIX C: the C++ DrawItem borders are in DIB px (× magnify, NOT × s — the design scale s
    // = 2 × magnify). SELECTED = a 2-DIB-px WHITE border on the FULL item rect; UNSELECTED =
    // a 1-DIB-px kiclrListBorder cyan rect INSET 1 DIB-px (Builder.cpp:1015-1021).
    const mag = this.d.magnify;
    const byUt = new Map(m.items.map((it) => [it.ut, it]));
    for (const rowEl of list.querySelectorAll('[data-buildpanel-item]')) {
      const row = rowEl as HTMLElement;
      const it = byUt.get(Number(row.dataset.buildpanelItem));
      if (it === undefined) continue;
      // selection border (DIB px × magnify): 2px white selected (full rect, inset 0), 1px
      // kiclrListBorder cyan rgb(84,160,172) inset 1px else.
      const frame = row.querySelector('[data-buildpanel-frame]') as HTMLElement | null;
      if (frame !== null) {
        if (it.selected) {
          frame.style.border = `${Math.max(1, Math.round(2 * mag))}px solid #fff`;
          frame.style.inset = '0';
        } else {
          frame.style.border = `${Math.max(1, Math.round(mag))}px solid rgb(84,160,172)`;
          frame.style.inset = `${Math.max(1, Math.round(mag))}px`;
        }
      }
      row.dataset.buildpanelSelected = it.selected ? '1' : '0';
      // disabled shadow (re-evaluated; usually static but God-mode/prereqs can change).
      const shade = row.querySelector('[data-buildpanel-shade]') as HTMLElement | null;
      if (shade !== null) shade.style.display = it.disabled ? 'block' : 'none';
      // queue-count overlay.
      const cnt = row.querySelector('[data-buildpanel-count]') as HTMLElement | null;
      if (cnt !== null) {
        if (it.queueCount > 0) {
          cnt.textContent = String(it.queueCount);
          cnt.style.display = '';
        } else cnt.style.display = 'none';
      }
      // the yellow build-progress bar (length = fraction × inner width). Builder.cpp insets it
      // by 2 DIB px each side (rc.left=x+2, rc.right=x+cx-2) → the full bar width = item width
      // − 2*(2*magnify) (DIB px, NOT × s — matches the itemRow inset).
      const prog = row.querySelector('[data-buildpanel-progress]') as HTMLElement | null;
      if (prog !== null) {
        if (it.buildProgress !== null) {
          const itemW = row.getBoundingClientRect().width;
          // getBoundingClientRect is 0 before layout / while the panel is momentarily hidden;
          // only (re)size the bar when we have a real width (else keep the last width) so an
          // in-flight build doesn't render a 0px (invisible) bar on the first frame(s).
          if (itemW > 0) {
            const innerW = itemW - 2 * (2 * mag); // item width minus the 2 DIB-px insets each side
            prog.style.width = `${Math.max(0, Math.round(innerW * it.buildProgress))}px`;
          }
          prog.style.display = 'block';
        } else prog.style.display = 'none';
      }
    }
  }

  // LIVE per-poll stat block refresh (UpdateUnitInfo / UpdateStructureInfo / UpdateUpgradeInfo:
  // labels + meters).
  private updateInfo(m: BuildPanelModel): void {
    const stage = this.stage;
    if (stage === null) return;
    const info = m.info;
    const set = (sel: string, text: string): void => {
      const e = stage.querySelector(sel) as HTMLElement | null;
      if (e !== null) e.textContent = text;
    };
    // the NAME: set + shrink-to-fit. The canvas LabelControl shows the FULL name (the long
    // R&D name "RESEARCH & DEVELOPMENT CENTER" runs nearly the whole panel) in the compact
    // bitmap font; the wider Eurostile web face overflows the box, so scaleX it down to fit
    // (anchored left) — matching the canvas, never clipped by the stage's overflow:hidden.
    const setName = (text: string): void => {
      const e = stage.querySelector('[data-buildpanel-name]') as HTMLElement | null;
      if (e === null) return;
      e.textContent = text;
      fitToWidth(e);
    };

    // RESEARCH variant (FORM E): ONLY name + cost row + description + prerequisites (E-R3). The
    // empty state ("NO UPGRADES AVAILABLE" in the name) hides Research + the cost/prereq labels.
    if (this.variant === 'research') {
      setName(info?.name ?? '');
      set('[data-buildpanel-value="cost"]', info?.cost ?? '');
      set('[data-buildpanel-description]', info?.description ?? '');
      set('[data-buildpanel-prerequisites]', info?.prerequisites ?? '');
      this.setPips('cost', info?.costMeter ?? 0, info?.meterMax ?? 0);
      const display = (sel: string, show: boolean): void => {
        const e = stage.querySelector(sel) as HTMLElement | null;
        if (e !== null) e.style.display = show ? '' : 'none';
      };
      // the COST label + COST value + COST meter (SetOwner hides them in the empty state — E-R3).
      const costLabelShown = info?.costLabelShown ?? !m.emptyState;
      display('[data-buildpanel-label="cost"]', costLabelShown);
      display('[data-buildpanel-value="cost"]', costLabelShown);
      display('[data-buildpanel-meter="cost"]', costLabelShown);
      // the PREREQUISITES label + value (SetOwner/UpdateUpgradeInfo Show — E-R3).
      const preShown = info?.prerequisitesShown ?? !m.emptyState;
      display('[data-buildpanel-prereq-label]', preShown);
      display('[data-buildpanel-prerequisites]', preShown);
      // the Research button visibility (E-R3/E-R4 — hidden in the empty state / disabled sel).
      display('[data-buildpanel-research]', m.orderShown);
      return;
    }

    setName(info?.name ?? '');
    set('[data-buildpanel-value="cost"]', info?.cost ?? '');
    set('[data-buildpanel-value="armor"]', info?.armor ?? '');
    set('[data-buildpanel-description]', info?.description ?? '');
    const mmax = info?.meterMax ?? 0;
    this.setPips('cost', info?.costMeter ?? 0, mmax);
    this.setPips('armor', info?.armorMeter ?? 0, mmax);
    if (this.variant === 'structure') {
      // P DEMAND / P SUPPLY (C-R6) — the structure stat rows (no speed/range exist).
      set('[data-buildpanel-value="powerDemand"]', info?.powerDemand ?? '');
      set('[data-buildpanel-value="powerSupply"]', info?.powerSupply ?? '');
      this.setPips('powerDemand', info?.powerDemandMeter ?? 0, mmax);
      this.setPips('powerSupply', info?.powerSupplyMeter ?? 0, mmax);
    } else {
      set('[data-buildpanel-value="speed"]', info?.speed ?? '');
      set('[data-buildpanel-value="range"]', info?.range ?? '');
      this.setPips('speed', info?.speedMeter ?? 0, mmax);
      this.setPips('range', info?.rangeMeter ?? 0, mmax);
    }
    this.setDamage(info?.damage ?? { infantry: 0, vehicle: 0, structure: 0 });
    // the limit label + Order button visibility (D-R5 — also gate-keyed in the rebuild,
    // but reflect it live in case it flips without a set change).
    const limit = stage.querySelector('[data-buildpanel-limit]') as HTMLElement | null;
    if (limit !== null) limit.style.display = m.limitReached ? 'block' : 'none';
    const order = stage.querySelector('[data-buildpanel-order]') as HTMLElement | null;
    if (order !== null) order.style.display = m.orderShown ? '' : 'none';
    const cancelOrder = stage.querySelector('[data-buildpanel-cancel-order]') as HTMLElement | null;
    if (cancelOrder !== null) cancelOrder.style.display = m.cancelOrderShown ? '' : 'none';
  }

  // ── widgets ────────────────────────────────────────────────────────────────────

  // a stat ROW: a left label (web font), a right value (web font), a real-bitmap pip/damage
  // meter. The canvas bitmap font is COMPACT, so "DAMAGE" / "P DEMAND" / "P SUPPLY" fit in the
  // x=30→x=64 (~34 design-unit) gap before the meter. The web font is wider — clip-fixed by
  // shrinking the row-label font + tightening letter-spacing + giving the label the full gap
  // width (labelX → meterX) so it never clips a word (gap fix).
  private statRow(
    stage: HTMLElement,
    label: string,
    y: number,
    key: string,
    kind: 'pip' | 'damage',
  ): void {
    const lbl = this.rowLabel(ROW.labelX, y, ROW.meterX - ROW.labelX - 1, label);
    lbl.dataset.buildpanelLabel = key;
    // a long label ("P DEMAND"/"P SUPPLY") overruns toward the meter; keep it ABOVE the value
    // (z1) and the pips (appended later) so it's never covered by them.
    lbl.style.zIndex = '2';
    stage.append(lbl);

    if (kind === 'pip') {
      // the VALUE (kidcArmorStrength/Cost/… — kifntShadow, RIGHT-aligned, forms.ini
      // box 136,22). The canvas LabelControl draws right-anchored and does NOT clip — a
      // wide word like "MEDIUM"/"HEAVY" simply overruns leftward over the meter tail (the
      // armor pips are short, so they don't visually collide). The DOM previously kept the
      // nominal 22-unit box with overflow:hidden, which clipped "MEDIUM". Fix: anchor the
      // RIGHT edge at the forms.ini box right (valueX+valueW=158) and extend the box LEFTWARD
      // with overflow visible so the full value renders (matching the canvas), above the meter.
      const right = ROW.valueX + ROW.valueW; // 158 — the forms.ini right anchor
      const valW = 44; // wide enough for "MEDIUM"; overruns left
      const value = this.label(right - valW, y, valW, '700', '#fff');
      value.style.textAlign = 'right';
      value.style.overflow = 'visible';
      value.style.zIndex = '1'; // over the meter (appended after)
      value.dataset.buildpanelValue = key;
      stage.append(value);
    }

    // the meter container — a plain block (the glyphs are inline-block, contiguous; spacing
    // is the canvas pip/damage spacing baked into each glyph's margin, NOT a flex gap).
    const meter = el('div', {
      position: 'absolute',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      lineHeight: '0',
    });
    if (kind === 'pip') {
      meter.dataset.buildpanelMeter = key;
      this.at(meter, ROW.meterX, y, ROW.meterW, ROW.meterH);
    } else {
      meter.dataset.buildpanelDamage = '1';
      this.at(meter, DAMAGE_METER.x, DAMAGE_METER.y, DAMAGE_METER.w, DAMAGE_METER.h);
    }
    stage.append(meter);
  }

  // a 0..100 pip meter → a row of REAL pip.png glyphs (the small cyan circle the canvas
  // PipMeterControl.OnPaint blits contiguously, x += sizPip.cx). render `count` pips (the
  // EXACT canvas GetPipCount — build-forms-spec D-R3, NOT re-derived from the percentage),
  // each sized native-px × the design scale so `count` pips occupy the SAME proportion of the
  // 71-wide meter as the canvas. Falls back to a CSS cyan circle when the bitmap is absent.
  private setPips(key: string, count: number, max: number): void {
    const stage = this.stage;
    if (stage === null) return;
    const meter = stage.querySelector(`[data-buildpanel-meter="${key}"]`) as HTMLElement | null;
    if (meter === null) return;
    const pip = this.d.art?.pip ?? null;
    if (pip != null && pip.cx > 0) {
      // native-px × s, tiled contiguously (the canvas x += sizPip.cx — no gap).
      this.tileGlyphs(meter, count, pip, 0);
    } else {
      // CSS fallback: a cyan circle sized so `max` circles span the 71-wide meter, with the
      // canvas's internal padding (circle smaller than its cell) so they read as spaced.
      const fullW = ROW.meterW * this.s;
      const cellW = max > 0 ? fullW / max : Math.max(2, Math.round(2 * this.s));
      this.fillCssPips(meter, count, cellW, COL_PIP);
    }
  }

  // the DAMAGE row → per-target-class glyph bitmaps (the canvas DamageMeterControl: a "star"
  // for infantry, a structure/house glyph, a vehicle glyph — damage_infantry/vehicle/structure
  // .png), blitted with a 1px gap (DrawPips px.v += sizPip.cx + 1). render the per-class count
  // (DamagePipCount, already computed). Falls back to per-class CSS glyphs when bitmaps absent.
  private setDamage(d: { infantry: number; vehicle: number; structure: number }): void {
    const stage = this.stage;
    if (stage === null) return;
    const meter = stage.querySelector('[data-buildpanel-damage]') as HTMLElement | null;
    if (meter === null) return;
    meter.replaceChildren();
    const art = this.d.art;
    // the three damage glyphs are extracted INDEPENDENTLY (main.ts extractAtlasArt, per-name),
    // so a partial atlas (infantry present, vehicle/structure missing) is representable — gate
    // on ALL THREE being real, else fall back, so tileGlyphs never dereferences a null bitmap.
    const real =
      (art?.damageInfantry?.cx ?? 0) > 0 &&
      (art?.damageVehicle?.cx ?? 0) > 0 &&
      (art?.damageStructure?.cx ?? 0) > 0;
    if (real) {
      this.tileGlyphs(meter, d.infantry, art!.damageInfantry!, kDamageGap, false);
      this.tileGlyphs(meter, d.vehicle, art!.damageVehicle!, kDamageGap, false);
      this.tileGlyphs(meter, d.structure, art!.damageStructure!, kDamageGap, false);
    } else {
      const s = this.s;
      const pipW = Math.max(2, Math.round(3 * s));
      const gap = Math.max(1, Math.round(kDamageGap * s));
      const addRow = (n: number, color: string): void => {
        for (let i = 0; i < n; i++) {
          meter.append(
            el('span', {
              display: 'inline-block',
              width: `${pipW}px`,
              height: `${pipW}px`,
              background: color,
              marginRight: `${gap}px`,
              borderRadius: '50%',
            }),
          );
        }
      };
      addRow(d.infantry, COL_DMG.infantry);
      addRow(d.vehicle, COL_DMG.vehicle);
      addRow(d.structure, COL_DMG.structure);
    }
  }

  // tile `count` copies of a meter-glyph BITMAP, sized GAME-PX × kGameScale (the `magnify`
  // factor — T3), pixelated, so each glyph lands at the IDENTICAL physical size the canvas
  // blits (the canvas paints these bitmaps at game px scaled by gameScale; the meter rect is
  // wider than the glyphs occupy in BOTH — the extra is empty background). `gapDesign` is the
  // inter-glyph gap in GAME px (0 for pips — contiguous, x += sizPip.cx; 1 for damage —
  // DrawPips's px.v += sizPip.cx + 1). `replace` clears the host first (true for a single pip
  // meter; false to append a damage class onto the shared row).
  private tileGlyphs(
    host: HTMLElement,
    count: number,
    bmp: Bitmap,
    gapDesign: number,
    replace = true,
  ): void {
    if (replace) host.replaceChildren();
    const mag = this.d.magnify;
    const w = Math.max(1, Math.round(bmp.cx * mag));
    const h = Math.max(1, Math.round(bmp.cy * mag));
    const gap = Math.round(gapDesign * mag);
    const url = this.glyphUrl(bmp);
    for (let i = 0; i < count; i++) {
      const img = el('img', {
        display: 'inline-block',
        width: `${w}px`,
        height: `${h}px`,
        imageRendering: 'pixelated',
        pointerEvents: 'none',
        verticalAlign: 'middle',
      }) as HTMLImageElement;
      if (gap > 0 && i < count - 1) img.style.marginRight = `${gap}px`;
      img.src = url;
      img.draggable = false;
      host.append(img);
    }
  }

  // the meter-glyph data-URL cache (keyed by the bitmap identity — the four meter bitmaps
  // are stable for the life of the view).
  private glyphUrl(bmp: Bitmap): string {
    let u = this.glyphCache.get(bmp);
    if (u === undefined) {
      u = rgbaToDataUrl(bmp);
      this.glyphCache.set(bmp, u);
    }
    return u;
  }

  private fillCssPips(meter: HTMLElement, cPips: number, cellW: number, color: string): void {
    meter.replaceChildren();
    // the circle is ~70% of its cell so contiguous cells read as SPACED circles (the canvas
    // pip.png is a small circle padded in its cell). border-radius:50% → a cyan circle.
    const dia = Math.max(2, Math.round(cellW * 0.7));
    const pad = Math.max(0, Math.round((cellW - dia) / 2));
    for (let i = 0; i < cPips; i++) {
      meter.append(
        el('span', {
          display: 'inline-block',
          width: `${dia}px`,
          height: `${dia}px`,
          background: color,
          borderRadius: '50%',
          marginRight: `${pad * 2}px`,
        }),
      );
    }
  }

  private label(x: number, y: number, w: number, weight: string, color: string): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * this.s)}px`,
      fontWeight: weight,
      color,
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      letterSpacing: '0.02em',
    });
    this.at(e, x, y, w);
    return e;
  }

  // a stat-row LABEL (COST/ARMOR/DAMAGE/SPEED/RANGE/P DEMAND/P SUPPLY). The web font is wider
  // than the canvas bitmap font, so the long labels ("DAMAGE", "P DEMAND", "P SUPPLY") clipped
  // in the nominal 22-unit box. Render them at a SMALLER font + NEGATIVE letter-spacing so they
  // fit the x=30→x=64 gap WITHOUT clipping (the compact canvas look). overflow visible so a word
  // is never truncated; the gap (34 units) is wide enough for the shrunk text to fit cleanly.
  private rowLabel(x: number, y: number, w: number, text: string): HTMLElement {
    const e = el('div', {
      // FIX B: the C++ stat labels (COST/ARMOR/DAMAGE/P DEMAND/P SUPPLY) are kifntShadow baked
      // WHITE, same as the values — so the row label is white, NOT the muted COL_LABEL gray.
      fontFamily: FONT,
      fontSize: `${Math.max(7, Math.round(4.6 * this.s))}px`,
      fontWeight: '600',
      color: '#fff',
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      letterSpacing: '-0.02em',
    });
    e.textContent = text;
    this.at(e, x, y, w);
    return e;
  }

  // FIX A: a header BITMAP button (Help "?" / Cancel "X") — the cyan-framed rounded button
  // ButtonControl blits (helpup/cancelup.png; *down on press). The bitmap fills the HELP/CANCEL
  // rect (× s, the design scale — same as the rest of the form chrome), image-rendering
  // pixelated, no flat-color background. The Help "?" looks ENABLED (its up bitmap IS the live
  // cyan frame) even though its click is a no-op. Falls back to iconBtn (the flat CSS box) when
  // the bitmap is absent (no atlas).
  private headerBtn(
    glyph: string,
    up: string | undefined,
    down: string | undefined,
    enabled: boolean,
    onClick: () => void,
  ): HTMLButtonElement {
    if (up === undefined || down === undefined) return this.iconBtn(glyph, enabled, onClick);
    const b = el('button', {
      padding: '0',
      border: 'none',
      background: 'transparent',
      boxSizing: 'border-box',
      backgroundImage: `url(${up})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
      imageRendering: 'pixelated',
      cursor: 'pointer',
    }) as HTMLButtonElement;
    b.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      b.style.backgroundImage = `url(${down})`;
    });
    const rel = (): void => {
      b.style.backgroundImage = `url(${up})`;
    };
    b.addEventListener('pointerup', rel);
    b.addEventListener('pointerleave', rel);
    b.addEventListener('pointercancel', rel);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      this.d.playSfx?.(ksfxGuiButtonTap);
      onClick();
    });
    return b;
  }

  // a small icon button (Help "?" / Cancel "X") in the top-right.
  private iconBtn(glyph: string, enabled: boolean, onClick: () => void): HTMLButtonElement {
    const b = el('button', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      border: 'none',
      borderRadius: '2px',
      boxSizing: 'border-box',
      fontFamily: FONT,
      fontSize: `${Math.round(7 * this.s)}px`,
      fontWeight: '700',
      color: enabled ? '#fff' : '#5a6a6e',
      background: enabled ? 'rgba(40,86,150,0.85)' : 'rgba(40,50,54,0.6)',
      cursor: enabled ? 'pointer' : 'default',
    }) as HTMLButtonElement;
    b.textContent = glyph;
    b.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });
    if (enabled)
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        this.d.playSfx?.(ksfxGuiButtonTap);
        onClick();
      });
    return b;
  }

  // a text button (Order/Cancel Order/Close) — the 9-slice composited bitmap frame with a
  // web-font label, or a CSS pill fallback (the SAME widget the confirm uses).
  private textBtn(
    label: string,
    wDesign: number,
    hDesign: number,
    onClick: () => void,
  ): HTMLButtonElement {
    const s = this.s;
    const w = wDesign * s,
      h = hDesign * s;
    const art = this.d.buttonArt;
    if (art === undefined) {
      const b = el('button', {
        fontSize: `${Math.max(8, Math.round(h * 0.5))}px`,
        fontWeight: '600',
        fontFamily: FONT_BUTTON,
        color: '#13191b',
        background: COL_ACCENT,
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
        width: `${w}px`,
        height: `${h}px`,
        whiteSpace: 'nowrap',
      }) as HTMLButtonElement;
      b.textContent = label;
      b.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        onClick();
      });
      return b;
    }
    const wArt = Math.round((w / h) * art.midUp.cy);
    const b = el('button', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4%',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      color: '#fff',
      fontWeight: '600',
      letterSpacing: '0.04em',
      fontFamily: FONT_BUTTON,
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
      width: `${w}px`,
      height: `${h}px`,
      fontSize: `${Math.max(8, Math.round(h * 0.5))}px`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
    }) as HTMLButtonElement;
    b.textContent = label;
    const setArt = (down: boolean): void => {
      b.style.backgroundImage = `url(${this.composite(art, wArt, down)})`;
    };
    setArt(false);
    b.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      setArt(true);
    });
    const rel = (): void => setArt(false);
    b.addEventListener('pointerup', rel);
    b.addEventListener('pointerleave', rel);
    b.addEventListener('pointercancel', rel);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return b;
  }

  private composite(art: ButtonArt, wArt: number, down: boolean): string {
    const key = `${wArt}:${down ? 1 : 0}`;
    let u = this.btnCache.get(key);
    if (u === undefined) {
      u = composeButtonDataUrl(art, wArt, down);
      this.btnCache.set(key, u);
    }
    return u;
  }

  private iconUrl(ut: number, icon: { cx: number; cy: number; rgba: Uint8Array }): string {
    let u = this.iconCache.get(ut);
    if (u === undefined) {
      u = rgbaToDataUrl(icon);
      this.iconCache.set(ut, u);
    }
    return u;
  }
}

// encode an RGBA buffer (ImageData byte order — the CPU present path's buffer) as a PNG
// data URL (the same encoding title-art.ts / the unit-menu uses).
function rgbaToDataUrl(art: { cx: number; cy: number; rgba: Uint8Array }): string {
  const canvas = document.createElement('canvas');
  canvas.width = art.cx;
  canvas.height = art.cy;
  const ctx = canvas.getContext('2d')!;
  const buf = new Uint8ClampedArray(art.rgba.length);
  buf.set(art.rgba);
  ctx.putImageData(new ImageData(buf, art.cx, art.cy), 0, 0);
  return canvas.toDataURL('image/png');
}

// Shrink an element's text horizontally (scaleX, anchored left) so it fits its box width
// without clipping — the canvas LabelControl shows the full text in the compact bitmap
// font; the wider Quadtrangle/Eurostile web faces would overflow, so we condense to fit.
// The element must be in the DOM (clientWidth/scrollWidth read) and have transformOrigin
// 'left …'. No-op when the text already fits or the box has no width yet.
function fitToWidth(e: HTMLElement): void {
  e.style.transform = ''; // reset so scrollWidth is the natural (unscaled) text width
  const box = e.clientWidth;
  const nat = e.scrollWidth;
  if (nat > box && box > 0) e.style.transform = `scaleX(${box / nat})`;
}
